
'use client';

import type { Table } from '@tanstack/react-table';
import type { ColumnConfig } from '../types';
import { calculateSummary } from '../utils/exportUtils';

interface SummaryRowProps {
  table: Table<any>;
  columns: ColumnConfig[];
  data: any[];
  /** First-cell text; defaults to "Total". */
  label?: string;
}

export const SummaryRow: React.FC<SummaryRowProps> = ({ table, columns, data, label = 'Total' }) => {
  const leafColumns = table.getVisibleLeafColumns();

  return (
    <tfoot className="bg-gray-50 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-600">
      <tr>
        {/* Label cell. It sits over the first column — often a 50-60px S.No — so it
            must clip rather than bleed across the neighbouring total. */}
        <td
          className="px-2 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 align-middle"
          style={{ overflow: 'hidden' }}
        >
          {/* "Total" rather than "Summary": the 60px S.No column clipped the longer
              word to "Sum…". */}
          <div className="truncate" title={label}>{label}</div>
        </td>

        {leafColumns.slice(1).map((column) => {
          const columnConfig = columns.find(col => 
            (col.field || col.columnName) === column.id
          );
          
          if (!columnConfig?.summaryType) {
            return <td key={column.id} className="px-2 sm:px-3 py-2"></td>;
          }

          const summaryValue = calculateSummary(
            data, 
            columnConfig.field!, 
            columnConfig.summaryType
          );

          let displayValue: string | number = summaryValue;

          // A column-supplied formatter wins, so the total reads exactly like the cells
          // above it (e.g. "6,00,000.00 Cr" rather than a bare "6,00,000").
          if (columnConfig.summaryRenderer) {
            displayValue = columnConfig.summaryRenderer(summaryValue);
          } else if (columnConfig.columnType === 'currency') {
            // INR by default — see the note in hooks/useDataGrid.tsx. 'DOLLAR' is opt-in.
            // The plain-number branch below already used en-IN, so a currency total was
            // formatted differently from the numbers it summed.
            const symbol = columnConfig.displayFormat === 'DOLLAR' ? '$' : '₹';
            const locale = columnConfig.displayFormat === 'DOLLAR' ? 'en-US' : 'en-IN';
            displayValue = `${symbol}${summaryValue.toLocaleString(locale)}`;
          } else if (columnConfig.columnType === 'number') {
            // Use Indian locale for number formatting (no currency symbol for plain numbers)
            displayValue = summaryValue.toLocaleString('en-IN');
          } else if (columnConfig.summaryType === 'avg') {
            displayValue = summaryValue.toFixed(2);
          }

          const alignmentClass = columnConfig.alignment === 'center' ? 'text-center' :
                                columnConfig.alignment === 'right' ? 'text-right' : 'text-left';

          return (
            <td
              key={column.id}
              className={`px-2 sm:px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 ${alignmentClass}`}
              style={{ overflow: 'hidden' }}
            >
              <div className="flex flex-col min-w-0">
                <span className="truncate" title={String(displayValue)}>{displayValue}</span>
                {columnConfig.summaryLabel && (
                  <span className="text-xs font-normal text-gray-500 dark:text-gray-400 truncate">{columnConfig.summaryLabel}</span>
                )}
              </div>
            </td>
          );
        })}
      </tr>
    </tfoot>
  );
};
